/**
 * Route Handler Component
 * Checks device type INSIDE Router context and renders appropriate app
 */

import React from 'react';
import { Routes, Route, Navigate, Outlet, useParams } from 'react-router-dom';
import { isMobileDevice } from '../utils/deviceDetection';
import MobileApp from './mobile/MobileApp';

// Import desktop pages
import Landing from '../pages/LandingPage';
import Dashboard from '../pages/Dashboard';
import CreateLeague from '../pages/CreateLeague';
import LeagueWelcome from '../pages/LeagueWelcome';
import LeagueDashboard from '../pages/LeagueDashboard';
import MyAccount from '../pages/MyAccount';
import PlayerProfile from '../pages/PlayerProfile';
import JoinLeague from '../pages/JoinLeague';
import SalaryContractSettings from '../pages/league-dashboard/salary-contract/SalaryContractSettings';
import KeeperSelection from '../pages/league-dashboard/KeeperSelection';
import LiveDraft from '../pages/league-dashboard/LiveDraft';
import { WatchList } from './WatchList';
import TradeProposalMockupViewer from './trades/mockups/TradeProposalMockupViewer';
import { dynastyTheme } from '../services/colorService';
import ProtectedRoute from './ProtectedRoute';
import { PlayerModalProvider } from '../contexts/PlayerModalContext';
import { LeagueProvider } from '../contexts/LeagueContext';
import { leaguesAPI } from '../services/apiService';

function LeagueShell() {
  const { leagueId } = useParams();
  // Root of every /leagues/:leagueId/* route. React Router only gives us the
  // URL param; the viewer's team in this league has to be fetched here so
  // child routes (WatchList, etc.) can render the player modal with a team
  // context. LeagueDashboard and similar pages also fetch this, but they're
  // children of this shell — by the time they mount, the modal may already
  // be opening with userTeamId undefined. Fetching once at the shell level
  // keeps the team a single prop drill from every descendant.
  //
  // Same pattern for the league object itself: fetched once here and
  // published via LeagueProvider so useLeague() works for every league-
  // scoped descendant (per Bible Rule 6 — league.current_season is the
  // source of truth for league-scoped operations).
  const [userTeamId, setUserTeamId] = React.useState(null);
  const [league, setLeague] = React.useState(null);
  React.useEffect(() => {
    if (!leagueId) return;
    let cancelled = false;
    leaguesAPI.getUserTeam(leagueId)
      .then(resp => {
        if (cancelled) return;
        const teamId = resp?.team?.team_id || resp?.team_id || null;
        setUserTeamId(teamId);
      })
      .catch(() => {});
    leaguesAPI.getLeagueDetails(leagueId)
      .then(resp => {
        if (cancelled) return;
        // Response shape: { success: bool, league: {...} }
        const leagueData = (resp?.success && resp?.league) || null;
        setLeague(leagueData);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [leagueId]);

  return (
    <LeagueProvider league={league}>
      <PlayerModalProvider leagueId={leagueId} userTeamId={userTeamId}>
        <Outlet />
      </PlayerModalProvider>
    </LeagueProvider>
  );
}

function AppRoutes() {
  const isMobile = isMobileDevice();

  // If mobile, render mobile app for ALL routes
  if (isMobile) {
    return <MobileApp />;
  }

  // Desktop routes (existing desktop app)
  return (
    <Routes>
      {/* Main Dashboard Route - Shows overlay for non-auth, full dashboard for auth */}
      <Route path="/" element={<Dashboard />} />
      <Route path="/dashboard" element={<Dashboard />} />

      {/* Landing page route if needed */}
      <Route path="/landing" element={<Landing />} />

      {/* Public invitation acceptance route - no authentication required initially */}
      <Route path="/join-league" element={<JoinLeague />} />

      {/* Protected Routes - Require Authentication */}
      <Route
        path="/create-league"
        element={
          <ProtectedRoute>
            <CreateLeague />
          </ProtectedRoute>
        }
      />

      <Route
        path="/my-account"
        element={
          <ProtectedRoute>
            <MyAccount />
          </ProtectedRoute>
        }
      />

      <Route
        path="/watchlist"
        element={
          <ProtectedRoute>
            <PlayerModalProvider leagueId={null} userTeamId={null}>
              <WatchList />
            </PlayerModalProvider>
          </ProtectedRoute>
        }
      />

      {/* Trade Mockup Viewer - Temporary route to view design options */}
      <Route
        path="/trade-mockup"
        element={
          <ProtectedRoute>
            <TradeProposalMockupViewer />
          </ProtectedRoute>
        }
      />

      {/* League routes - nested under LeagueShell which provides PlayerModalProvider */}
      <Route path="/leagues/:leagueId" element={<ProtectedRoute><LeagueShell /></ProtectedRoute>}>
        <Route index element={<LeagueDashboard />} />
        <Route path="welcome" element={<LeagueWelcome />} />
        <Route
          path="salary-contract-settings"
          element={
            <div style={{ background: dynastyTheme.utils.gradient('page'), minHeight: '100vh' }}>
              <SalaryContractSettings />
            </div>
          }
        />
        <Route
          path="keepers"
          element={
            <div style={{ background: dynastyTheme.utils.gradient('page'), minHeight: '100vh' }}>
              <KeeperSelection />
            </div>
          }
        />
        <Route path="draft/live" element={<LiveDraft />} />
        <Route path="players/:playerId" element={<PlayerProfile />} />
      </Route>

      {/* Global Player Profile Route (no league context) */}
      <Route
        path="/player/:playerId"
        element={
          <ProtectedRoute>
            <PlayerProfile />
          </ProtectedRoute>
        }
      />

      {/* Redirect unknown routes to dashboard if authenticated, otherwise to landing */}
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

export default AppRoutes;
